import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiErrorBody } from './types';

const baseURL = import.meta.env.VITE_API_URL ?? '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL,
  // The refresh token is an httpOnly cookie; without this it is never sent.
  withCredentials: true,
});

/**
 * The access token is held in memory only. Putting it in localStorage would
 * make it readable by any script on the page, and it buys nothing: the
 * httpOnly refresh cookie already restores the session across reloads.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

/**
 * A request that survives the page being closed.
 *
 * axios cannot set `keepalive`, and the browser cancels ordinary requests once
 * a page goes away — which is exactly the moment the exam most needs to report
 * what happened. sendBeacon would survive too but cannot carry an
 * Authorization header, so this is fetch with the same token the interceptor
 * uses.
 */
export function postKeepalive(path: string, body: unknown): void {
  try {
    void fetch(`${baseURL}${path}`, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // A page on its way out is not a place to handle errors.
  }
}
export function onSessionExpired(fn: () => void) {
  onSessionLost = fn;
}

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

/**
 * A single in-flight refresh, shared by every caller that needs one at the same
 * moment — the 401 retry path and the cold-load restore alike. Without this, a
 * dashboard firing four parallel requests on a stale token would start four
 * refreshes; since refresh tokens rotate one-use, the losers are read as replay
 * and the session dies. Every refresh in the client goes through this function
 * for that reason — never call `/auth/refresh` directly.
 */
let refreshing: Promise<string | null> | null = null;

export async function refreshAccessToken(): Promise<string | null> {
  refreshing ??= axios
    .post<{ accessToken: string }>(`${baseURL}/auth/refresh`, null, { withCredentials: true })
    .then((res) => {
      accessToken = res.data.accessToken;
      return accessToken;
    })
    .catch(() => {
      accessToken = null;
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const isAuthCall = original?.url?.includes('/auth/');

    if (error.response?.status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
      onSessionLost?.();
    }
    return Promise.reject(error);
  },
);

/** Pulls the human-readable message out of an API error, whatever its shape. */
export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (axios.isAxiosError<ApiErrorBody>(err)) {
    return err.response?.data?.error?.message ?? err.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

export function errorDetails<T = unknown>(err: unknown): T | undefined {
  if (axios.isAxiosError<ApiErrorBody>(err)) return err.response?.data?.error?.details as T;
  return undefined;
}

export function errorCode(err: unknown): string | undefined {
  if (axios.isAxiosError<ApiErrorBody>(err)) return err.response?.data?.error?.code;
  return undefined;
}
